import { useCallback, useEffect, useState } from "react";

import {
  type PCPSameAsReferrer,
  type ProgramID,
  ProgramStatus,
  type ProviderDetails,
  type ProviderSkipped,
} from "@/api/generated/models";
import { useUpdateUserProgramCareTeam } from "@/api/mutations/programs";
import { useGetUserProgram } from "@/api/queries/programs";
import { YStack } from "@/components";
import { useUserProgression } from "@/conversation/sidebar/UserProgression/UserProgressionContext";
import { captureException } from "@/sentry";
import { PCPCollectionScreen } from "./PCPCollectionScreen";
import { ReferralCollectionScreen } from "./ReferralCollectionScreen";

export const CareTeamCollectionFlow = ({
  onContinue,
  isPending = false,
  onBackButtonChange,
}: {
  onContinue: () => Promise<void>;
  isPending?: boolean;
  onBackButtonChange?: (show: boolean, onPress?: () => void) => void;
}) => {
  const program = useUserProgression()?.progression?.metadata?.program as
    | ProgramID
    | undefined;
  const { data: userProgram } = useGetUserProgram(program);
  // a lapsed program's answers belong to an enrollment that's over, so they don't prefill the form
  const savedResponse =
    userProgram?.status === ProgramStatus.lapsed
      ? undefined
      : userProgram?.careTeam;
  const updateCareTeam = useUpdateUserProgramCareTeam();
  const [page, setPage] = useState(1);
  const [draftReferral, setDraftReferral] = useState<
    ProviderDetails | ProviderSkipped
  >();
  const referrer = draftReferral ?? savedResponse?.referral;
  const handleBack = useCallback(() => setPage(1), []);

  useEffect(() => {
    onBackButtonChange?.(page === 2, handleBack);
    return () => onBackButtonChange?.(false);
  }, [page, handleBack, onBackButtonChange]);

  const save = async (
    pcp: ProviderDetails | ProviderSkipped | PCPSameAsReferrer,
  ) => {
    if (!program || !referrer) return;
    try {
      await updateCareTeam.mutateAsync({
        program,
        data: { pcp, referral: referrer },
      });
      await onContinue();
    } catch (error) {
      captureException(error, {
        extra: { context: "CareTeamCollectionFlow.save", program },
      });
    }
  };

  return (
    <>
      <YStack flex={1} display={page === 1 ? "flex" : "none"}>
        <ReferralCollectionScreen
          initialAnswer={savedResponse?.referral}
          onContinue={(answer) => {
            setDraftReferral(answer);
            setPage(2);
          }}
        />
      </YStack>
      <YStack flex={1} display={page === 2 ? "flex" : "none"}>
        {referrer && (
          <PCPCollectionScreen
            initialAnswer={draftReferral ? undefined : savedResponse?.pcp}
            referrer={referrer}
            onContinue={save}
            isPending={isPending || updateCareTeam.isPending}
          />
        )}
      </YStack>
    </>
  );
};
